import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import parse from 'html-react-parser';
import { type ReactElement, useCallback, useRef, useState } from 'react';

import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';

import { deleteItemsBySelectionId, resolveSelectionId } from './deleteItems.queries';
import {
    cleanKeys,
    type DeleteItemsSelection,
    resolveDeleteConfirmBody,
} from './deleteItemsSelection';

type DeletedItemsResult = {
    primaryKeys: string[];
    allMatchingQuery: boolean;
};

type UseDeleteItemsConfirmOptions = {
    onDeleted?: (result: DeletedItemsResult) => void;
};

type UseDeleteItemsConfirmResult = {
    requestDelete: (selection: DeleteItemsSelection) => void;
    dialog: ReactElement;
};

type ItemsCachePage = {
    data?: Array<{ primaryKey__?: string }>;
};

type ItemsCache = {
    pages?: ItemsCachePage[];
};

/** Drop known keys from the React list. The `deleted` channel event refetches the rest. */
const removeDeletedItemsFromCache = (queryClient: QueryClient, primaryKeys: string[]) => {
    if (primaryKeys.length === 0) {
        return;
    }
    const toRemove = new Set(primaryKeys);
    const allQueries = queryClient.getQueriesData<ItemsCache>({ queryKey: ['items'] });
    allQueries.forEach(([key, oldData]) => {
        if (!oldData?.pages) {
            return;
        }
        let changed = false;
        const pages = oldData.pages.map((page) => {
            const data = page.data ?? [];
            const next = data.filter((item) => !item.primaryKey__ || !toRemove.has(item.primaryKey__));
            if (next.length === data.length) {
                return page;
            }
            changed = true;
            return { ...page, data: next };
        });
        if (changed) {
            queryClient.setQueryData(key, { ...oldData, pages });
        }
    });
};

/** Usage count is still an Angular figure. Query deletes have no known count. */
const notifyUsageAfterDelete = (keyCount: number) => {
    if (keyCount < 1) {
        return;
    }
    try {
        const $rootScope = getRootScope(getInjector());
        $rootScope.$broadcast('updateOrganizationUsageLimit', -keyCount);
    } catch (error) {
        logger.error('Failed to update the organization usage count after delete', error);
    }
};

const selectionTableClass = 'tw-w-full tw-border-collapse tw-border-x-0 tw-text-t-sm tw-text-t-primary';
const selectionRowClass = 'odd:tw-bg-fill-tertiary even:tw-bg-fill-primary tw-border-y tw-border-x-0 tw-border-solid tw-border-tertiary';

const DeleteItemsConfirmBody = ({ selection }: { selection: DeleteItemsSelection | null }) => {
    const { t } = useTranslation();
    const body = selection ? resolveDeleteConfirmBody(selection) : { kind: 'all' as const };
    const messageKey = body.kind === 'keys'
        ? 'CONFIRM_DELETE.ITEM'
        : body.kind === 'query'
            ? 'CONFIRM_DELETE.ALL_ITEMS.PARAMETERS'
            : 'CONFIRM_DELETE.ALL_ITEMS';

    return (
        <>
            <p className="tw-text-t-primary lg-font-dynamic">
                {parse(t(messageKey))}
            </p>
            {body.kind === 'keys' && body.primaryKeys.length === 1 && (
                <ul data-testid="delete-items-keys">
                    <li>{body.primaryKeys[0]}</li>
                </ul>
            )}
            {body.kind === 'keys' && body.primaryKeys.length > 1 && (
                <div className="tw-max-h-40 tw-overflow-auto" data-testid="delete-items-keys">
                    <table className={selectionTableClass}>
                        <tbody>
                            {body.primaryKeys.map((primaryKey, index) => (
                                <tr
                                    key={`${index}-${primaryKey}`}
                                    className={selectionRowClass}
                                >
                                    <td className="tw-border-x-0 tw-text-t-secondary tw-w-5 tw-py-1 tw-px-2">
                                        {index + 1}
                                    </td>
                                    <td className="tw-border-x-0 tw-text-t-primary tw-py-1 tw-px-2">
                                        {primaryKey}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {body.kind === 'query' && (
                <div className="tw-max-h-40 tw-overflow-auto">
                    <table
                        data-testid="delete-items-query"
                        summary="Search parameters"
                        className={selectionTableClass}
                    >
                        <tbody>
                            {body.entries.map((entry) => (
                                <tr
                                    key={entry.key}
                                    className={selectionRowClass}
                                >
                                    <td className="tw-border-x-0 tw-text-t-secondary tw-py-1 tw-px-2">
                                        <strong>{entry.key}</strong>
                                    </td>
                                    <td className="tw-border-x-0 tw-text-t-primary tw-py-1 tw-px-2">
                                        {entry.value}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

const useDeleteItemsConfirm = ({
    onDeleted,
}: UseDeleteItemsConfirmOptions = {}): UseDeleteItemsConfirmResult => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const onDeletedRef = useRef(onDeleted);
    onDeletedRef.current = onDeleted;
    const [pending, setPending] = useState<DeleteItemsSelection | null>(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const confirmingRef = useRef(false);

    const requestDelete = useCallback((selection: DeleteItemsSelection) => {
        setPending(selection);
    }, []);

    const close = useCallback(() => {
        if (confirmingRef.current) {
            return;
        }
        setPending(null);
    }, []);

    const confirm = useCallback(async () => {
        if (!pending || confirmingRef.current) {
            return;
        }
        confirmingRef.current = true;
        setIsConfirming(true);
        try {
            const selectionId = await resolveSelectionId(pending);
            await deleteItemsBySelectionId(selectionId);
            const keyed = cleanKeys(pending.primaryKeys);
            const keysToRemove = keyed.length > 0 ? keyed : cleanKeys(pending.confirmPrimaryKeys);
            removeDeletedItemsFromCache(queryClient, keysToRemove);
            notifyUsageAfterDelete(keyed.length);
            toast.success(keyed.length === 1 ? 'ITEM_DELETED' : 'ITEMS_DELETED');
            onDeletedRef.current?.({
                primaryKeys: keysToRemove,
                allMatchingQuery: keyed.length === 0,
            });
            setPending(null);
        } catch (error) {
            logger.error('Failed to delete items', error);
            toast.error('ERROR_TECHNICAL');
        } finally {
            confirmingRef.current = false;
            setIsConfirming(false);
        }
    }, [pending, queryClient, toast]);

    const dialog = (
        <ConfirmationModal
            show={pending !== null}
            title="CONFIRM_DELETE"
            discardBtnText="ACTION.DELETE"
            closeBtnText="CANCEL"
            btnVariant="destructive"
            isConfirmDisabled={isConfirming}
            onSubmit={() => {
                void confirm();
            }}
            onClose={close}
        >
            <DeleteItemsConfirmBody selection={pending} />
        </ConfirmationModal>
    );

    return { requestDelete, dialog };
};

export type { DeletedItemsResult };
export { DeleteItemsConfirmBody, useDeleteItemsConfirm };
